import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, money } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function PaymentResult({ cancelled = false }: { cancelled?: boolean }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState<any>(cancelled ? { payment_status: "cancelled" } : null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    if (state?.payment_status === "paid" || attempts >= 6) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setState(data);
      } catch {
        setState({ payment_status: "error" });
      }
      setAttempts((a) => a + 1);
    }, attempts === 0 ? 300 : 2000);
    return () => clearTimeout(t);
  }, [attempts, cancelled, sessionId, state]);

  const paid = state?.payment_status === "paid";
  const pending = !cancelled && !paid && attempts < 6 && state?.payment_status !== "error";

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-ivory px-4" data-testid="payment-result-page">
      <div className="w-full max-w-md rounded-3xl border border-brand-line bg-white p-10 text-center shadow-lift">
        <Logo stacked />
        <div className="mt-8">
          {pending && <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand-blue" data-testid="payment-pending" />}
          {paid && <CheckCircle2 className="mx-auto h-14 w-14 text-brand-emerald" data-testid="payment-success" />}
          {(cancelled || (!pending && !paid)) && <XCircle className="mx-auto h-14 w-14 text-amber-500" data-testid="payment-cancelled" />}
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold text-brand-navy">
          {pending ? "Confirming your payment…" : paid ? "Funds secured in escrow" : cancelled ? "Payment cancelled" : "Payment not confirmed yet"}
        </h1>
        <p className="mt-2 text-sm text-brand-slate">
          {paid && state.amount != null ? `${money(state.amount, state.currency)} for ${state.service_name} is held safely. Our routing team will assign a verified caregiver and you'll see updates on your dashboard.` :
            pending ? "This usually takes a few seconds." : cancelled ? "No money was taken. You can book again whenever you're ready." : "If you were charged, the booking will appear on your dashboard shortly."}
        </p>
        {paid && state.booking_id && <p className="mt-3 text-xs text-brand-slate" data-testid="payment-booking-id">Booking ref: {state.booking_id}</p>}
        <Link to="/dashboard" className="mt-8 inline-flex rounded-full bg-brand-blue px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-blueHover" data-testid="payment-back-dashboard">Back to dashboard</Link>
      </div>
    </div>
  );
}
